import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { listBlogTeasers, DESTINATION_AIRPORT } from "@/data/blogs";
import { useSEO, breadcrumbsSchema, SITE_URL } from "@/lib/seo";

function JournalCard({ post }) {
  return (
    <article
      className="bg-white border border-[#E0DDD6] overflow-hidden lift"
      data-testid={`journal-post-${post.slug}`}
    >
      <Link to={`/blog/${post.slug}`} className="block group">
        <div className="aspect-[16/10] overflow-hidden">
          <img
            src={post.hero}
            alt={post.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        </div>
      </Link>
      <div className="p-6 md:p-8">
        <p className="tcp-overline text-[#C29B62] mb-3">
          {post.read_minutes} min read · {post.published_at}
        </p>
        <Link to={`/blog/${post.slug}`} className="block hover:text-[#C29B62] transition-colors">
          <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-tight text-[#1C1C1C]">
            {post.title}
          </h3>
        </Link>
        <p className="font-serif italic text-lg text-[#666460] mt-2">{post.subtitle}</p>
        <p className="text-sm text-[#666460] mt-4 leading-relaxed">{post.excerpt}</p>
        <div className="mt-6 flex flex-wrap gap-2">
          {post.destinations.map((d) => {
            // Each chip now links to /destinations/CODE — the editorial page
            // for that city — so SEO crawlers and humans land on a page that
            // can convert (search widget + live fares) rather than a fragment
            // inside the long-form post.
            const airport = DESTINATION_AIRPORT[d.anchor];
            const href = airport ? `/destinations/${airport.code}` : `/blog/${post.slug}#${d.anchor}`;
            return (
              <Link
                key={d.anchor}
                to={href}
                data-testid={`journal-chip-${post.slug}-${d.anchor}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs tracking-[0.16em] uppercase border border-[#E0DDD6] text-[#666460] hover:border-[#C29B62] hover:text-[#C29B62] transition-colors"
              >
                <MapPin size={12} weight="fill" /> {d.name}
              </Link>
            );
          })}
        </div>
        <Link
          to={`/blog/${post.slug}`}
          data-testid={`journal-read-${post.slug}`}
          className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase text-[#C29B62] hover:text-[#A88550]"
        >
          Read the guide <ArrowRight size={14} />
        </Link>
      </div>
    </article>
  );
}

export default function Journal() {
  const posts = listBlogTeasers();

  useSEO({
    title: "Journal — Travel Stories | TheCityPair",
    description:
      "Editor-picked destination guides from TheCityPair's travel desk — six in-depth itineraries spanning Asia, Europe, the Pacific, the Caribbean, Scandinavia, and the United Kingdom.",
    canonical: "/journal",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": `${SITE_URL}/journal`,
        name: "The Journal — Travel Stories",
        description: "Curated destination guides by TheCityPair editorial desk.",
        url: `${SITE_URL}/journal`,
        hasPart: posts.map((p) => ({
          "@type": "Article",
          headline: p.title,
          url: `${SITE_URL}/blog/${p.slug}`,
          image: p.hero,
          description: p.excerpt,
        })),
      },
      breadcrumbsSchema([
        { name: "Home", url: "/" },
        { name: "Journal", url: "/journal" },
      ]),
    ],
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#F7F5F0] min-h-screen" data-testid="journal-page">
      <div className="bg-[#0A0A0B] relative"><Navbar dark /><div className="h-24" /></div>

      {/* Editorial header */}
      <header className="px-6 md:px-12 lg:px-24 pt-16 md:pt-24 pb-12 md:pb-16">
        <div className="max-w-3xl">
          <p className="tcp-overline text-[#C29B62] mb-4">The Journal</p>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tighter leading-[0.95] text-[#1C1C1C]">
            Travel <em className="italic">stories</em>, curated by our desk.
          </h1>
          <p className="text-[#666460] mt-6 max-w-xl leading-relaxed">
            Editor-picked destination guides — the places our clients ask about most, with the
            experiences worth the flight. New stories every month.
          </p>
        </div>
      </header>

      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {posts.map((p) => <JournalCard key={p.slug} post={p} />)}
        </div>
      </section>

      <Footer />
    </div>
  );
}
